import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { Textarea } from "@/components/ui/textarea";
import { Banner } from "@/components/ui/banner";
import { useCreateClient } from "@/hooks/queries/useCreateClient";
import {
  clientIntakeSchema,
  MIN_DOB,
  todayUtcIso,
  type ClientIntakeValues,
} from "@/routes/clients/client-intake-schema";
import { toCreateClientError, type ClientErrorBanner } from "@/routes/clients/client-error";

// Client intake (UX Screen 03, Story 5.7 + spec-gh-352). Renders the agreed intake
// form. PHI (first/last/middle name, DOB, notes) is encrypted server-side; gender
// is non-PHI metadata stored plaintext. Optional text fields (middle initial,
// notes) are omitted from the request when blank, so the API never receives an
// empty-string PHI value to encrypt.
//
// (The "Consent & privacy" and "Record mode" selects were dropped on 2026-06-16 —
// both were inert metadata with no downstream behaviour, and "Record mode" implied
// a pseudonymization guarantee the system does not enforce since all client PHI is
// always encrypted. See spec-gh-352 / Franklin#352.)
//
// The screen's "Save & start evaluation" CTA is intentionally NOT built — client
// creation is decoupled from starting an evaluation (FR6/FR13, PM-confirmed); a
// single "Save" persists the record and returns to the list. The human-readable
// Client ID (CITS-NNNNNN, FR12) is a fast-follow and is not shown here yet.
//
// Form discipline mirrors the auth screens: RHF + zodResolver, double-submit
// guarded on mutation.isPending, banner cleared on submit, 422 field errors
// attached via form.setError without dropping form state, and PHI never logged or
// placed in a URL.

const GENDER_OPTIONS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
];

export function ClientIntakeScreen() {
  const navigate = useNavigate();
  const [banner, setBanner] = React.useState<ClientErrorBanner | null>(null);

  const form = useForm<ClientIntakeValues>({
    resolver: zodResolver(clientIntakeSchema),
    defaultValues: {
      firstName: "",
      middleInitial: "",
      lastName: "",
      dob: "",
      gender: "",
      notes: "",
    },
  });

  const createClient = useCreateClient();

  const onSubmit = form.handleSubmit((values) => {
    if (createClient.isPending) return;
    setBanner(null);
    createClient.mutate(
      {
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        // Optional PHI / metadata: omit when blank so the API stores NULL, not
        // an encrypted empty string (middle_initial/notes) or "" (gender).
        middle_initial: values.middleInitial.trim() || undefined,
        dob: values.dob,
        gender: values.gender || undefined,
        notes: values.notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          // The hook invalidates the list cache; return to it (the new client shows masked).
          navigate("/clients");
        },
        onError: (error) => {
          const mapped = toCreateClientError(error);
          if (mapped.fieldErrors.firstName) {
            form.setError("firstName", { message: mapped.fieldErrors.firstName });
          }
          if (mapped.fieldErrors.lastName) {
            form.setError("lastName", { message: mapped.fieldErrors.lastName });
          }
          if (mapped.fieldErrors.dob) {
            form.setError("dob", { message: mapped.fieldErrors.dob });
          }
          setBanner(mapped.banner);
        },
      },
    );
  });

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">Create new client record</h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Structured intake for the person / client before starting an AMPS evaluation.
          You can reveal their name on the clients list when you need it.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Client details</CardTitle>
          <CardDescription>
            Names, date of birth and notes are stored as encrypted PHI; gender is
            stored as record metadata.
          </CardDescription>
        </CardHeader>

        {banner ? (
          <Banner variant={banner.variant} className="mt-4">
            {banner.message}
          </Banner>
        ) : null}

        <form className="mt-5 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField label="First name" error={form.formState.errors.firstName?.message} required>
              <Input autoComplete="off" {...form.register("firstName")} />
            </FormField>

            <FormField
              label="Middle initial"
              error={form.formState.errors.middleInitial?.message}
            >
              <Input autoComplete="off" {...form.register("middleInitial")} />
            </FormField>

            <FormField label="Last name" error={form.formState.errors.lastName?.message} required>
              <Input autoComplete="off" {...form.register("lastName")} />
            </FormField>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Birth date"
              error={form.formState.errors.dob?.message}
              helper="Must be a past date, on or after 1900-01-01."
              required
            >
              {/* Native picker bounds mirror the Zod rules (min 1900-01-01, max today,
                  UTC) so the date control can't surface a value the schema will reject. */}
              <Input type="date" min={MIN_DOB} max={todayUtcIso()} {...form.register("dob")} />
            </FormField>

            <FormField label="Gender" htmlFor="gender">
              <Controller
                name="gender"
                control={form.control}
                render={({ field }) => (
                  <SearchableCombobox
                    id="gender"
                    options={GENDER_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select…"
                    searchAriaLabel="Gender options"
                    clearAriaLabel="Clear gender selection"
                    showSearch={false}
                  />
                )}
              />
            </FormField>
          </div>

          <FormField label="Notes" error={form.formState.errors.notes?.message}>
            <Textarea rows={4} {...form.register("notes")} />
          </FormField>

          <div className="mt-1 flex items-center gap-3">
            <Button type="submit" variant="primary" disabled={createClient.isPending}>
              {createClient.isPending ? "Saving…" : "Save"}
            </Button>
            <Button asChild variant="link">
              <Link to="/clients">Cancel</Link>
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}

export default ClientIntakeScreen;
